import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { setConfigurationLoginModalOpen } from '../../../store/actions'
import { updateKeywordSearchParameters } from '../../../store/actions/search'
import { formatPdfLink, trimFileLink } from '../../cdl-react-components/utilities/files'
import { logEvent } from '../../../utilities'
import { CONTENT_MODE } from '../../../utilities/constants'
import { graphqlFetch } from '../../cdl-react-components/utilities'

import ArticleDesktop from './ArticleDesktop'
import ArticleMobile from './ArticleMobile'

const Article = (props) => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const user = useSelector(_ => _.data.user)
    const authenticated = useSelector(_ => _.data.authenticated)
    const contentMode = useSelector(_ => _.data.configuration.contentMode)
    const [article, setArticle] = useState()

    useEffect(() => {
        fetchArticle(id)
    }, [id])

    const fetchArticle = (article_id) => {
        /* Updating the state in this function will cause another update to occur.
           As such this function will be called again causing an infinite loop.
           Only update the state if it needs to be updated. - rrogers 2018-07-05 */
        if (!article ||
            article.id !== parseInt(article_id)
        ) {
            logEvent({ 'Article_Landing_Page_Viewed': article_id })
            graphqlFetch('wcms3', `
                query {
                    articles (
                        where: { id: { eq: ${article_id} } }
                    ) {
                        items {
                            ages: contentArticleAges {
                                age {
                                    id 
                                    name
                                }
                            }
                            area
                            area_of_interest: contentArticleAreaOfInterests {
                                areaofinterest {
                                    id 
                                    name
                                }
                            }
                            article_type: contentArticleArticleTypes {
                                articletype {
                                    id
                                    name
                                }
                            }
                            authors: contentArticleAuthors {
                                author {
                                    id
                                    name
                                }
                            }
                            block {
                                name
                            }
                            coming_soon: comingSoon
                            ep_type: epType {
                                name
                            }
                            featured_article: featuredArticle
                            fields: contentArticleFields {
                                field {
                                    id
                                    name
                                }
                            }
                            figure_count: figureCount
                            figure_types: contentArticleFigureTypes {
                                figuretype {
                                    id  
                                    name
                                }
                            }
                            fluids: contentArticleFluids {
                                fluid {
                                    id
                                    name
                                }
                            }
                            free_article: freeArticle
                            id
                            image
                            keywords: contentArticleKeywords {
                                keyword {
                                    id
                                    name
                                }
                            }
                            map_image: mapImage
                            map_unit: mapUnit {
                                name
                            }
                            meridian {
                                name
                            }
                            nid
                            operators: contentArticleOperators {
                                operator {
                                    id
                                    name
                                }
                            }
                            page_count: pageCount
                            pdf
                            pdf_size_field: pdfFileSize
                            plays: contentArticlePlays {
                                play {
                                    id
                                    name
                                }
                            }
                            publication_date: publicationDate
                            publication_order: publicationOrder
                            published
                            quad {
                                name
                            }
                            quarter_volumn: quarterVolumn {
                                name
                            }
                            range {
                                name
                            }
                            regions_or_topics: contentArticleRegionsOrTopics {
                                regiontopic {
                                    id 
                                    name
                                }
                            }
                            relevance_tags: contentArticleRelevanceTags {
                                relevancetag {
                                    id
                                    name
                                }
                            }
                            sample_article: sampleArticle
                            subdivision {
                                name
                            }
                            subtitle
                            synopsis
                            table_count: tableCount
                            thumbnails
                            title
                            township {
                                name
                            }
                            word_count: wordCount
                            zone_of_interest: zoneOfInterest {                                
                                name
                            }   
                        }
                    }
                }`)
                .then(res => {
                    if (res.data.articles.items.length === 0) 
                        return navigate('/')
                    else {
                        const article = res.data.articles.items[0]

                        article.block = article.block?.name
                        article.ep_type = article.ep_type?.name
                        article.map_unit = article.map_unit?.name
                        article.meridian = article.meridian?.name
                        article.quad = article.quad?.name
                        article.quarter_volumn = article.quarter_volumn?.name
                        article.range = article.range?.name
                        article.subdivision = article.subdivision?.name
                        article.township = article.township?.name
                        article.zone_of_interest = article.zone_of_interest?.name

                        article.ages = article.ages?.map(x => x.age)
                        article.area_of_interest = article.area_of_interest?.map(x => x.areaofinterest)
                        article.article_type = article.article_type?.map(x => x.articletype)
                        article.authors = article.authors?.map(x => x.author)
                        article.fields = article.fields?.map(x => x.field)
                        article.figure_types = article.figure_types?.map(x => x.figuretype)
                        article.fluids = article.fluids?.map(x => x.fluid)
                        article.keywords = article.keywords?.map(x => x.keyword)
                        article.operators = article.operators?.map(x => x.operator)
                        article.plays = article.plays?.map(x => x.play)
                        article.regions_or_topics = article.regions_or_topics?.map(x => x.regiontopic)
                        article.relevance_tags = article.relevance_tags?.map(x => x.relevancetag)

                        setArticle(article)
                    }
                })
        }
    }

    const commaSeporate = (arr, seporator = ', ') => arr.map(x => x.name).join(seporator)    
    const commaSeporateComponents = (arr, key, seporator = ', ') =>
        arr.map((x, i) => (
            <span 
                key={`csv-${key}--${i}`} 
                className="link" 
                onClick={() => dispatch(updateKeywordSearchParameters(x.name))}>
                {x.name}{i < arr.length - 1 && seporator}
            </span>))    

    const buildPdfLocation = () => formatPdfLink(window.location.search, article.id, trimFileLink(article.pdf))
    const buildSamplePdfLocation = () => formatPdfLink(window.location.search, article.id, trimFileLink(article.sample_article))

    const login = () => {
        dispatch(setConfigurationLoginModalOpen(true))
    }

    return article ? (
        contentMode === CONTENT_MODE.MOBILE ?
            (<ArticleMobile
                article={article} 
                handleLogin={login} 
                authenticated={authenticated}
                user={user}
                link={buildPdfLocation()}
                sampleLink={buildSamplePdfLocation()}
                commaSeporate={commaSeporate}
                commaSeporateComponents={commaSeporateComponents} />) :
            (<ArticleDesktop 
                article={article} 
                handleLogin={login} 
                authenticated={authenticated}
                user={user}
                link={buildPdfLocation()}
                sampleLink={buildSamplePdfLocation()}
                commaSeporate={commaSeporate}
                commaSeporateComponents={commaSeporateComponents} />)
    ): null
}

export default Article

